import { createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import {
  booleanOptions,
  BooleanValue,
} from 'common/genericOptions';
import { EventoFinanceiro, GrupoAcesso, TipoEventoFinanceiro } from 'common/genericTypes';
import UploadCsv from 'components/instrumentos/uploadCsv';
import { RadioButton, Section, SmSpace } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { addAlert } from 'store/modules/snackBar';
import { connect } from 'react-redux';
import { RootState } from 'store';
import {
  updateAmortizacoes,
  updateDatasIncorporacaoJuros,
  updateDatasPagamentoJuros,
  updateField,
} from 'store/modules/notaComercial/eventos';
import { v4 as uuidv4 } from 'uuid';
import { PagamentoJurosSection } from './jurosSection';
import { DatesSection } from './datesSection';
import { labelsEventosFinanceiros as labels } from 'common/registro/labels';
import FluxoMonitorSection from './fluxoMonitorSection';
import { useValidarCampos } from '../useValidarCampos';
import { AmortizacoesSection } from 'components/instrumentos/registro/eventosFinanceiros/amortizacoesSection';


interface EventosFinanceirosGroup {
  [key: string]: EventoFinanceiro[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    banner: {
      marginLeft: '-40px',
      padding: '40px',
      backgroundColor: `${theme.palette.primary.main}1A`,
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  criteriosRemuneracao: state.notaComercialCadastro.criteriosRemuneracao,
  eventos: state.notaComercialCadastro.eventos,
  highlightErrors: state.notaComercialCadastro.highlightErrors,
  typeOfRegister: state.notaComercialCadastro.typeOfRegister,
  grupoAcesso: state.auth.usuario.grupoAcesso,
});

const mapDispatchToProps = {
  updateDatasIncorporacaoJuros,
  updateDatasPagamentoJuros,
  updateField,
  updateAmortizacoes,
  addAlert,
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const Eventos: React.FC<Props> = (props) => {
  const {
    eventos: {
      percentualAmortizacaoSobre,
      percentualAmortizacaoPassivelAlteracao,
      amortizacoes,
      datasPagamentoJuros,
      datasIncorporacaoJuros,
      resgateAntecipadoFacultativo,
      modalidadeAmortizacao,
    },
    criteriosRemuneracao: { dataEmissao, dataVencimento, dataInicioRentabilidade },
    grupoAcesso,
    updateField,
    updateDatasPagamentoJuros,
    updateAmortizacoes,
    updateDatasIncorporacaoJuros,
    addAlert,
    highlightErrors,
  } = props;
  const classes = useStyles();

  const [groupedEventosFinanceiros, setGroupedEventosFinanceiros] = useState<EventosFinanceirosGroup>({});

  useEffect(() => {
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.Amortizacao]) {
      updateAmortizacoes(
        groupedEventosFinanceiros[TipoEventoFinanceiro.Amortizacao].map((amortizacao) => ({
          id: uuidv4(),
          percentualAmortizacao: amortizacao.percentual ?? undefined,
          dataAmortizacao: amortizacao.data,
        }))
      );
    }
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.PagamentoJuros]) {
      updateDatasPagamentoJuros(
        groupedEventosFinanceiros[TipoEventoFinanceiro.PagamentoJuros].map((pagamento) => ({ id: uuidv4(), date: pagamento.data }))
      );
    }
    if (groupedEventosFinanceiros[TipoEventoFinanceiro.IncorporacaoJuros]) {
      updateDatasIncorporacaoJuros(
        groupedEventosFinanceiros[TipoEventoFinanceiro.IncorporacaoJuros].map((incorporacao) => ({
          id: uuidv4(),
          date: incorporacao.data,
        }))
      );
    }
  }, [groupedEventosFinanceiros, updateDatasIncorporacaoJuros, updateDatasPagamentoJuros]);

  const handleUploadCsv = (eventosFinanceiros: EventoFinanceiro[]) => {
    try {
      const groupedEventosFinanceiros = eventosFinanceiros.reduce<EventosFinanceirosGroup>((acc, curr) => {
        if (acc[curr.tipo]) {
          acc[curr.tipo].push(curr);
        } else {
          acc[curr.tipo] = [curr];
        }
        return acc;
      }, {});
      setGroupedEventosFinanceiros(groupedEventosFinanceiros);
    } catch (error) { }
  };

  return (
    <div>
      <div className={classes.banner}>
        <Grid container spacing={4} alignItems="center">
          <Grid item md={4}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <AnnouncementIcon fontSize={'large'} />
              </Grid>
              <Grid item xs>
                <Typography variant="h3">{'Por que não agilizar esse processo?'}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item container md={4} justify="center">
            <div>
              <Typography variant={'subtitle1'}>{'Upload de Eventos Financeiros'}</Typography>
              <SmSpace />
            </div>
            <UploadCsv
              onChange={handleUploadCsv}
              onError={(error) => addAlert({ severity: 'error', message: error })}
            />
          </Grid>
        </Grid>
      </div>
      {[GrupoAcesso.CadastroOperacoes, GrupoAcesso.Operacoes].includes(grupoAcesso) && <FluxoMonitorSection />}
      <AmortizacoesSection
        percentualAmortizacaoSobre={percentualAmortizacaoSobre ?? ''}
        percentualAmortizacaoPassivelAlteracao={!!percentualAmortizacaoPassivelAlteracao}
        modalidadeAmortizacao={modalidadeAmortizacao}
        amortizacoes={amortizacoes}
        onUpdateField={updateField}
        onUpdateAmortizacoes={updateAmortizacoes}
        minDate={dataEmissao}
        maxDate={dataVencimento}
        useValidarCampos={useValidarCampos}
        shouldHighlightErrors={highlightErrors}
      />
      <PagamentoJurosSection
        onUpdateDates={updateDatasPagamentoJuros}
        dates={datasPagamentoJuros ?? []}
        minDate={dataEmissao}
        maxDate={dataVencimento}
      />
      <DatesSection
        title={labels.incorporacaoJuros}
        label={labels.dataIncorporacaoJuros}
        onUpdateDates={updateDatasIncorporacaoJuros}
        dates={datasIncorporacaoJuros ?? []}
        minDate={dataInicioRentabilidade}
        maxDate={dataVencimento}
      />
      <Section title={'Resgate Antecipado Facultativo'}>
        <RadioButton
          label={labels.resgateAntecipadoFacultativo}
          options={booleanOptions}
          value={resgateAntecipadoFacultativo ? BooleanValue.Yes : BooleanValue.No}
          onChange={(resgateAntecipadoFacultativo: string) =>
            updateField({
              name: 'resgateAntecipadoFacultativo',
              value: resgateAntecipadoFacultativo === BooleanValue.Yes,
            })
          }
          inline
        />
      </Section>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Eventos);
